import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ConfigurationStatus {
  // Critical items (blocking)
  hasEstablishmentName: boolean;
  hasActiveTables: boolean;
  hasActiveTimeSlots: boolean;
  isNotPaused: boolean;
  
  // Recommended items (non-blocking)
  hasEmail: boolean;
  hasPhone: boolean;
  hasAddress: boolean;
  hasGoogleMaps: boolean;
  hasReviewLink: boolean;
  hasNotificationEmail: boolean;
  hasLogo: boolean;
  
  // Counts for display
  activeTablesCount: number;
  activeTimeSlotsCount: number;
  
  // Overall status
  isReadyForProduction: boolean;
  criticalIssuesCount: number;
  recommendedIssuesCount: number;
  
  // Loading state
  isLoading: boolean;
  // Set to true when the last check could not produce a reliable status
  hasCheckError: boolean;
}

const DISMISSED_KEY = 'onboardingWidgetDismissed';
const DISMISSED_EXPIRY_DAYS = 7;

/** Treat empty strings and whitespace-only strings as missing values. */
const isFilled = (value?: string | null): boolean =>
  typeof value === 'string' && value.trim().length > 0;

export function useConfigurationCheck(establishmentId: string | null) {
  const [status, setStatus] = useState<ConfigurationStatus>({
    hasEstablishmentName: true,
    hasActiveTables: true,
    hasActiveTimeSlots: true,
    isNotPaused: true,
    hasEmail: true,
    hasPhone: true,
    hasAddress: true,
    hasGoogleMaps: true,
    hasReviewLink: true,
    hasNotificationEmail: true,
    hasLogo: true,
    activeTablesCount: 0,
    activeTimeSlotsCount: 0,
    isReadyForProduction: true,
    criticalIssuesCount: 0,
    recommendedIssuesCount: 0,
    isLoading: true,
    hasCheckError: false,
  });

  // Track the last establishment we produced a reliable status for.
  // When establishment changes, we reset so we don't show stale data.
  const lastResolvedEstablishmentId = useRef<string | null>(null);

  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    const stored = localStorage.getItem(DISMISSED_KEY);
    if (!stored) return false;
    
    try {
      const { dismissedAt, establishmentId: dismissedEstId } = JSON.parse(stored);
      // Check if dismissal is still valid (within expiry period) and for same establishment
      const dismissedDate = new Date(dismissedAt);
      const expiryDate = new Date(dismissedDate);
      expiryDate.setDate(expiryDate.getDate() + DISMISSED_EXPIRY_DAYS);
      
      if (new Date() > expiryDate) {
        localStorage.removeItem(DISMISSED_KEY);
        return false;
      }
      
      return dismissedEstId === establishmentId;
    } catch {
      localStorage.removeItem(DISMISSED_KEY);
      return false;
    }
  });

  const checkConfiguration = useCallback(async () => {
    if (!establishmentId) {
      setStatus(prev => ({ ...prev, isLoading: false, hasCheckError: false }));
      return;
    }

    // Reset reference when establishment changes
    if (lastResolvedEstablishmentId.current !== establishmentId) {
      lastResolvedEstablishmentId.current = null;
    }

    try {
      // Fetch all required data in parallel
      const [
        establishmentRes,
        tablesRes,
        slotsRes,
        reservationSettingsRes,
        notificationSettingsRes,
      ] = await Promise.all([
        supabase
          .from('establishments')
          .select('name, email, phone, address, google_maps_url, google_business_url, tripadvisor_review_url, thefork_review_url, facebook_review_url, logo_url')
          .eq('id', establishmentId)
          .maybeSingle(),
        supabase
          .from('tables')
          .select('id', { count: 'exact', head: true })
          .eq('establishment_id', establishmentId)
          .eq('is_active', true),
        supabase
          .from('time_slots')
          .select('id', { count: 'exact', head: true })
          .eq('establishment_id', establishmentId)
          .eq('is_active', true),
        supabase
          .from('reservation_settings')
          .select('is_paused')
          .eq('establishment_id', establishmentId)
          .maybeSingle(),
        supabase
          .from('notification_settings')
          .select('recipient_email')
          .eq('establishment_id', establishmentId),
      ]);

      // CRITICAL: if any critical query failed or returned an unreliable result,
      // do NOT downgrade the UI to "no tables / no slots". Keep the last known
      // status and flag the error so the widget can stay hidden.
      const establishment = establishmentRes.data as any;
      const establishmentError = establishmentRes.error;
      const tablesError = tablesRes.error;
      const slotsError = slotsRes.error;
      const reservationSettingsError = reservationSettingsRes.error;
      const notificationsError = notificationSettingsRes.error;

      const tablesCount = tablesRes.count;
      const slotsCount = slotsRes.count;

      const hasCriticalError =
        !!establishmentError ||
        !!tablesError ||
        !!slotsError ||
        !establishment ||
        tablesCount === null ||
        tablesCount === undefined ||
        slotsCount === null ||
        slotsCount === undefined;

      if (hasCriticalError) {
        if (establishmentError) console.warn('[ConfigurationCheck] establishment error:', establishmentError);
        if (tablesError) console.warn('[ConfigurationCheck] tables error:', tablesError);
        if (slotsError) console.warn('[ConfigurationCheck] slots error:', slotsError);

        // Preserve previous status: only flip loading & error flags.
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          hasCheckError: true,
        }));
        return;
      }

      // notification_settings is non-critical; if it errored, treat as "unknown
      // but assume configured" so we don't show a false recommendation.
      const notificationData = (notificationSettingsRes.data || []) as Array<{ recipient_email: string | null }>;
      const hasNotificationEmail = notificationsError
        ? true
        : notificationData.some(n => isFilled(n.recipient_email));

      // reservation_settings is non-critical for "is_paused"; if it errored, assume not paused.
      const reservationSettings = reservationSettingsRes.data as any;
      const isNotPaused = reservationSettingsError ? true : !reservationSettings?.is_paused;

      const hasReviewLink =
        isFilled(establishment?.google_business_url) ||
        isFilled(establishment?.tripadvisor_review_url) ||
        isFilled(establishment?.thefork_review_url) ||
        isFilled(establishment?.facebook_review_url);

      // Critical issues
      const hasEstablishmentName =
        isFilled(establishment?.name) && establishment.name !== 'Mon établissement';
      const hasActiveTables = (tablesCount ?? 0) > 0;
      const hasActiveTimeSlots = (slotsCount ?? 0) > 0;

      // Recommended issues
      const hasEmail = isFilled(establishment?.email);
      const hasPhone = isFilled(establishment?.phone);
      const hasAddress = isFilled(establishment?.address);
      const hasGoogleMaps = isFilled(establishment?.google_maps_url);
      const hasLogo = isFilled(establishment?.logo_url);

      const criticalIssuesCount = [
        !hasEstablishmentName,
        !hasActiveTables,
        !hasActiveTimeSlots,
        !isNotPaused,
      ].filter(Boolean).length;

      const recommendedIssuesCount = [
        !hasEmail,
        !hasPhone,
        !hasReviewLink,
        !hasNotificationEmail,
      ].filter(Boolean).length;

      lastResolvedEstablishmentId.current = establishmentId;

      setStatus({
        hasEstablishmentName,
        hasActiveTables,
        hasActiveTimeSlots,
        isNotPaused,
        hasEmail,
        hasPhone,
        hasAddress,
        hasGoogleMaps,
        hasReviewLink,
        hasNotificationEmail,
        hasLogo,
        activeTablesCount: tablesCount ?? 0,
        activeTimeSlotsCount: slotsCount ?? 0,
        isReadyForProduction: criticalIssuesCount === 0,
        criticalIssuesCount,
        recommendedIssuesCount,
        isLoading: false,
        hasCheckError: false,
      });
    } catch (error) {
      console.error('Error checking configuration:', error);
      // Network failure or unexpected exception: preserve prior status and flag error.
      setStatus(prev => ({ ...prev, isLoading: false, hasCheckError: true }));
    }
  }, [establishmentId]);

  useEffect(() => {
    checkConfiguration();
  }, [checkConfiguration]);

  const dismissWidget = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify({
      dismissedAt: new Date().toISOString(),
      establishmentId,
    }));
    setIsDismissed(true);
  }, [establishmentId]);

  const showWidget = useCallback(() => {
    localStorage.removeItem(DISMISSED_KEY);
    setIsDismissed(false);
  }, []);

  // Determine if widget should be shown.
  // We require:
  // - the check finished loading,
  // - the user hasn't dismissed it,
  // - the latest check did NOT error (avoid the "false yellow" flash),
  // - we have a resolved status for the current establishment,
  // - there's at least one real issue.
  const shouldShowWidget =
    !status.isLoading &&
    !isDismissed &&
    !status.hasCheckError &&
    lastResolvedEstablishmentId.current === establishmentId &&
    (status.criticalIssuesCount > 0 || status.recommendedIssuesCount > 0);

  return {
    status,
    isDismissed,
    shouldShowWidget,
    dismissWidget,
    showWidget,
    refetch: checkConfiguration,
  };
}
